import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import teacherService from '../../services/api/teacher';
import studentService from '../../services/api/student';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useConfirm } from '../../hooks/useConfirm';
import { useGenericSearch } from '../../hooks/useSearch';

import { getServerUrl } from '../../config/apiConfig';
import TeacherLessonCard from '../../components/cards/TeacherLessonCard';
import {
  Play,
  Plus,
  Video,
  Users,
  TrendingUp,
  Eye,
  Activity,
  Filter,
  ArrowUpDown,
  Search,
  BarChart3,
  FileVideo,
  X,
  ShieldCheck,
  Download,
  FileText,
  ChevronLeft
  ,Loader
} from 'lucide-react';

import {
  getLessonDuration,
  formatDuration,
  getLessonViews,
  getVideoEmbedUrl,
  getLessonData
} from '../../utils/videoUtils';
import { getSecureVideoUrl } from '../../utils/secureVideoUtils';

// PDF preview helper component used by the teacher lessons modal
const PdfPreviewInModal = ({ lessonId }) => {
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!lessonId) return;
    setIsLoading(true);
    setError(null);

    teacherService.getFileUrl(lessonId)
      .then(data => {
        const url = data?.fileUrl || data?.url;
        if (url) {
          setPdfUrl(url);
        } else {
          setError('لم يتم العثور على الملف');
        }
      })
      .catch(err => {
        setError(err.message || 'فشل تحميل الملف');
      })
      .finally(() => setIsLoading(false));
  }, [lessonId]);

  if (isLoading) return (
    <div className="aspect-video bg-gray-50 rounded-3xl flex items-center justify-center">
      <Loader size={32} className="animate-spin text-primary-600" />
    </div>
  );

  if (error || !pdfUrl) return (
    <div className="aspect-video bg-red-50 rounded-3xl flex items-center justify-center">
      <p className="text-red-500 font-bold">{error || 'تعذّر تحميل الملف'}</p>
    </div>
  );

  return (
    <div className="aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl ring-8 ring-gray-50">
      <iframe
        src={`https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`}
        className="w-full h-full border-0"
        title="PDF Viewer"
      />
    </div>
  );
};


const Lessons = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const { success: toastSuccess, error: toastError } = useToast();
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lessonsPerPage] = useState(6);
  const [lessonAnalytics, setLessonAnalytics] = useState({}); // Store Bunny Analytics

  // Enhanced filter states

  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');



  // Helper Functions



  // جلب إحصائيات المدرس

  const { data: statistics } = useQuery(

    'teacher-statistics',

    async () => {

      const response = await teacherService.getTeacherStatistics();

      return response.data || response; // Handle both data structures

    }

  );



  // جلب الكورسات الخاصة بالمدرس

  useQuery(
    'teacher-courses',
    async () => {
      const response = await teacherService.getTeacherCourses();
      let coursesArray = [];
      if (Array.isArray(response)) {
        coursesArray = response;
      } else if (response.data && Array.isArray(response.data)) {
        coursesArray = response.data;
      } else if (response.courses && Array.isArray(response.courses)) {
        coursesArray = response.courses;
      } else if (response.data && response.data.courses && Array.isArray(response.data.courses)) {
        coursesArray = response.data.courses;
      }
      return coursesArray;

    }

  );



  // جلب بيانات الفلاتر الخاصة بالمدرس
  const { data: teacherSubjectsData } = useQuery(
    'teacher-subjects',
    () => teacherService.getSubjects(),
    {
      retry: false,
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  // Extract unique stages and grades from teacher's subjects
  const filters = useMemo(() => {
    const subjectsArray = Array.isArray(teacherSubjectsData) ? teacherSubjectsData : teacherSubjectsData?.subjects || [];
    
    if (!subjectsArray || subjectsArray.length === 0) {
      return { stages: [], subjects: [], grades: [], courses: [] };
    }

    const stagesSet = new Set();
    const gradesSet = new Set();
    const coursesSet = new Set();
    const stages = [];
    const grades = [];
    const courses = [];

    subjectsArray.forEach(subject => {
      // Add stage if not already added - use stage_name as unique identifier
      if (subject.stage_name && !stagesSet.has(subject.stage_name)) {
        stagesSet.add(subject.stage_name);
        stages.push({
          id: subject.stage_name, // Use stage_name as ID for consistency
          name: subject.stage_name
        });
      }

      // Add grade if not already added
      if (subject.grade_id && !gradesSet.has(subject.grade_id)) {
        gradesSet.add(subject.grade_id);
        grades.push({
          id: subject.grade_id,
          name: subject.grade_name,
          stage_name: subject.stage_name // Use stage_name instead of stage_id
        });
      }

      // Add course if not already added
      if (subject.course_id && !coursesSet.has(subject.course_id)) {
        coursesSet.add(subject.course_id);
        courses.push({
          id: subject.course_id,
          title: subject.course_title,
          subject_id: subject.teacher_subject_id,
          subject_name: subject.subject_name
        });
      }
    });

    return {
      stages: stages.sort((a, b) => a.name.localeCompare(b.name)),
      subjects: subjectsArray.map(s => ({
        id: s.teacher_subject_id,
        name: s.subject_name,
        grade_name: s.grade_name,
        stage_name: s.stage_name // Add stage_name for filtering
      })).sort((a, b) => a.name.localeCompare(b.name)),
      grades: grades.sort((a, b) => a.name.localeCompare(b.name)),
      courses: courses.sort((a, b) => a.title.localeCompare(b.title))
    };
  }, [teacherSubjectsData]);

  // Extract grades based on selected stage
  const allGrades = useMemo(() => {
    if (!selectedStage) return filters?.grades || [];
    return filters?.grades?.filter(grade => grade.stage_name === selectedStage) || [];
  }, [selectedStage, filters?.grades]);

  // Filter subjects based on selected stage
  const filteredSubjects = useMemo(() => {
    if (!selectedStage) return filters?.subjects || [];
    return filters?.subjects?.filter(subject => subject.stage_name === selectedStage) || [];
  }, [selectedStage, filters?.subjects]);

  // Extract courses based on selected subject
  const allCourses = useMemo(() => {
    if (!selectedSubject) return filters?.courses || [];
    return filters?.courses?.filter(course => course.subject_id === parseInt(selectedSubject)) || [];
  }, [selectedSubject, filters?.courses]);

  // جلب جميع الدروس (بدون pagination) لحساب الإجمالي الدقيق
  const { data: allLessonsData } = useQuery(
    'teacher-all-lessons',
    () => {
      // جلب جميع الدروس بدون pagination
      return teacherService.getTeacherLessonsOriginal('?limit=9999');
    },
    {
      retry: false,
      staleTime: 60000,
      refetchOnWindowFocus: false
    }
  );

  const allLessons = useMemo(() => {
    let allLessonsArray = [];
    
    if (Array.isArray(allLessonsData)) {
      allLessonsArray = allLessonsData;
    } else if (allLessonsData?.data && Array.isArray(allLessonsData.data)) {
      allLessonsArray = allLessonsData.data;
    } else if (allLessonsData?.lessons && Array.isArray(allLessonsData.lessons)) {
      allLessonsArray = allLessonsData.lessons;
    }
    
    return allLessonsArray;
  }, [allLessonsData]);

  // جلب الدروس مع الفلاتر من السيرفر (Pagination حقيقي)
  const { data: lessonsData, isLoading } = useQuery(
    ['teacher-lessons', selectedStage, selectedGrade, selectedSubject, selectedCourse, currentPage],
    () => {
      const params = new URLSearchParams();
      
      // Server-side filters
      if (selectedStage) params.append('stage', selectedStage);
      if (selectedGrade) params.append('grade', selectedGrade);
      if (selectedSubject) params.append('subject', selectedSubject);
      if (selectedCourse) params.append('course', selectedCourse);
      
      // Server-side pagination
      params.append('page', currentPage);
      params.append('limit', lessonsPerPage);
      
      return teacherService.getTeacherLessonsOriginal(`?${params}`);
    },
    {
      retry: false,
      staleTime: 30000,
      refetchOnWindowFocus: false,
      keepPreviousData: true
    }
  );

  // Server-side pagination data
  const totalCount = lessonsData?.meta?.total || lessonsData?.meta?.totalCount || 0;

  // Client-side search and sorting على ALL lessons (جميع الدروس وليس الصفحة الحالية فقط)
  const {
    searchTerm: hookSearchTerm,
    filteredData: searchedLessons,
    setSearchTerm: setHookSearchTerm,
  } = useGenericSearch(allLessons, ['title', 'description']);

  // Client-side sorting
  const sortedLessons = useMemo(() => {
    if (!searchedLessons || searchedLessons.length === 0) {
      return [];
    }
    
    const sorted = [...searchedLessons];
    
    switch (sortBy) {
      case 'title':
        return sorted.sort((a, b) => {
          const comparison = a.title?.localeCompare(b.title, 'ar') || 0;
          return sortOrder === 'asc' ? comparison : -comparison;
        });
      case 'views':
        return sorted.sort((a, b) => {
          // Use Bunny Analytics if available, otherwise fall back to getLessonViews
          const viewsA = lessonAnalytics[a.id] ?? getLessonViews(a) ?? 0;
          const viewsB = lessonAnalytics[b.id] ?? getLessonViews(b) ?? 0;
          return sortOrder === 'asc' ? viewsA - viewsB : viewsB - viewsA;
        });
      case 'date':
      default:
        return sorted.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        });
    }
  }, [searchedLessons, sortBy, sortOrder, lessonAnalytics]);

  // Final data for display
  const displayLessons = sortedLessons;

  // Apply pagination to search/filtered results (client-side pagination)
  const paginatedDisplayLessons = useMemo(() => {
    const start = (currentPage - 1) * lessonsPerPage;
    const end = start + lessonsPerPage;
    return displayLessons.slice(start, end);
  }, [displayLessons, currentPage, lessonsPerPage]);

  // Update total pages based on filtered/searched results
  const searchedTotalPages = useMemo(() => {
    return Math.ceil(displayLessons.length / lessonsPerPage) || 1;
  }, [displayLessons, lessonsPerPage]);

  // Calculate total views ONLY from Bunny Analytics (accurate data from ALL lessons)
  // Fallback to database only if Bunny data not available
  const totalViewsFromAnalytics = useMemo(() => {
    if (!allLessons || allLessons.length === 0) return 0;
    
    let bunnyTotal = 0;
    let hasBunnyData = false;

    // First, sum only Bunny Analytics data
    allLessons.forEach(lesson => {
      if (lessonAnalytics[lesson.id] !== null && lessonAnalytics[lesson.id] !== undefined) {
        bunnyTotal += parseInt(lessonAnalytics[lesson.id]) || 0;
        hasBunnyData = true;
      }
    });

    // If we have Bunny data, use it; otherwise fall back to database
    if (hasBunnyData && bunnyTotal > 0) {
      return bunnyTotal;
    }

    // Fallback to database views from ALL lessons
    return allLessons.reduce((total, lesson) => {
      const views = parseInt(getLessonViews(lesson) ?? 0) || 0;
      return total + views;
    }, 0);
  }, [allLessons, lessonAnalytics]);

  // Handle search input change (client-side فقط)
  const handleSearch = (e) => {
    setHookSearchTerm(e.target.value);
  };

  // Apply server-side filters when they change
  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [selectedStage, selectedGrade, selectedSubject, selectedCourse]);

  // Extract displayed lesson IDs only (avoid re-fetching on every sort/analytics change)
  const displayedLessonIds = useMemo(() => {
    return displayLessons.map(lesson => lesson.id);
  }, [displayLessons]);

  // Extract all lesson IDs (avoid re-fetching on every sort/analytics change)
  const allLessonIds = useMemo(() => {
    return allLessons.map(lesson => lesson.id);
  }, [allLessons]);

  // Fetch Bunny Analytics for displayed lessons (with fallback to getLessonViews)
  // Note: We need lessonAnalytics in deps to check cache, but this is safe because we return early
  // if all lessons are already cached, preventing infinite loops
  useEffect(() => {
    if (!displayedLessonIds || displayedLessonIds.length === 0) {
      return;
    }

    const fetchAnalytics = async () => {
      const analytics = {};
      
      // Only fetch for lessons we don't have analytics for yet
      const lessonsToFetch = displayedLessonIds.filter(id => !(id in lessonAnalytics));
      
      if (lessonsToFetch.length === 0) {
        return; // Already have all analytics
      }
      
      for (const lessonId of lessonsToFetch) {
        try {
          const data = await studentService.getLessonAnalytics(lessonId);
          analytics[lessonId] = data?.data?.stats?.views || null;
        } catch (error) {
          analytics[lessonId] = null;
        }
      }
      
      setLessonAnalytics(prev => ({ ...prev, ...analytics }));
    };

    fetchAnalytics();
  }, [displayedLessonIds, lessonAnalytics]);

  // Fetch ALL lessons analytics separately for accurate total views from Bunny only
  // Note: We need lessonAnalytics in deps to check cache, but this is safe because we return early
  // if all lessons are already cached, preventing infinite loops
  useEffect(() => {
    if (!allLessonIds || allLessonIds.length === 0) {
      return;
    }

    const fetchAllAnalytics = async () => {
      const analytics = {};
      
      // Only fetch for lessons we don't have analytics for yet
      const lessonsToFetch = allLessonIds.filter(id => !(id in lessonAnalytics));
      
      if (lessonsToFetch.length === 0) {
        return; // Already have all analytics
      }
      
      for (const lessonId of lessonsToFetch) {
        try {
          const data = await studentService.getLessonAnalytics(lessonId);
          analytics[lessonId] = data?.data?.stats?.views || null;
        } catch (error) {
          analytics[lessonId] = null;
        }
      }
      
      setLessonAnalytics(prev => ({ ...prev, ...analytics }));
    };

    fetchAllAnalytics();
  }, [allLessonIds, lessonAnalytics]);



  // Event Handlers



  const handleDeleteLesson = async (lesson) => {

    const confirmed = await confirm({
      title: 'حذف الدرس نهائياً',
      message: `هل أنت متأكد من حذف الدرس "${lesson.title}"؟ لا يمكن التراجع عن هذا الإجراء.`,
      type: 'danger'

    });

    

    if (confirmed) {

      try {
        await teacherService.deleteLesson(lesson.id);
        // Invalidate paginated and aggregated lesson queries so UI refreshes
        queryClient.invalidateQueries('teacher-lessons');
        queryClient.invalidateQueries('teacher-all-lessons');
        queryClient.invalidateQueries('teacher-statistics');

        // Optimistically remove the lesson from the cached 'teacher-all-lessons' data
        try {
          queryClient.setQueryData('teacher-all-lessons', (old) => {
            if (!old) return old;
            // Old may be array or object with .data or .lessons
            if (Array.isArray(old)) return old.filter(l => l.id !== lesson.id);
            if (old.data && Array.isArray(old.data)) return { ...old, data: old.data.filter(l => l.id !== lesson.id) };
            if (old.lessons && Array.isArray(old.lessons)) return { ...old, lessons: old.lessons.filter(l => l.id !== lesson.id) };
            return old;
          });
        } catch (err) {
          // ignore optimistic update errors
        }

        toastSuccess('تم حذف الدرس بنجاح');
      } catch (error) {
        toastError('فشل في عملية الحذف');
      }
    }

  };



  const handlePlayVideo = async (lesson) => {
    try {
      // الحصول على رابط الفيديو الآمن من السيرفر
      const result = await getSecureVideoUrl(lesson.id);
      const videoUrl = typeof result === 'string' ? result : result.url;
      const videoSource = typeof result === 'object' ? result.videoSource : 'unknown';
      
      setSelectedVideo({
        ...lesson,
        video_url: videoUrl, // استخدام الرابط الآمن فقط
        video_source: videoSource // نوع الفيديو
      });
      
      setShowVideoModal(true);
    } catch (error) {
      // عرض رسالة خطأ للمستخدم
      toastError(error.message || 'فشل في الحصول على رابط التشغيل');
    }
  };



  const handleCloseVideoModal = () => {

    setSelectedVideo(null);

    setShowVideoModal(false);

  };



  // Pagination logic (server-side)
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Reset to page 1 when search changes (client-side فقط)
  useEffect(() => {
    setCurrentPage(1);
  }, [hookSearchTerm]);


  if (isLoading) {

    return (

      <div className="flex flex-col items-center justify-center min-h-[500px]">

        <LoadingSpinner size="large" />

        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل مكتبة دروسك...</p>

      </div>

    );

  }



  return (

    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">

      

      {/* Header Section */}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
       
          <div>
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight">مكتبة دروسي</h1>
            <p className="text-gray-500 mt-2 font-medium flex items-center gap-2">
                <ShieldCheck className="text-primary-600" size={20} />
                تحكم في المحتوى التعليمي وتابع تفاعل الطلاب مع فيديوهاتك

            </p>

          </div>

          <div className="flex flex-col sm:flex-row gap-4">

            <button

              onClick={() => navigate('/teacher/create-lesson')}

              className="bg-primary-600 text-white px-8 py-3.5 rounded-2xl font-black text-sm hover:bg-primary-700 transition-all shadow-xl shadow-primary-100 flex items-center justify-center gap-2 transform active:scale-95"

            >

              <Plus size={20} />

              إنشاء درس جديد

            </button>

          </div>

        </div>

      </div>



      {/* Statistics Quick View */}

      {statistics && (

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

          {[

            { label: 'إجمالي الطلاب', value: statistics.totalStudents, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },

            { label: 'إجمالي الدروس', value: statistics.totalLessons, icon: Video, color: 'text-emerald-600', bg: 'bg-emerald-50' },

            { label: 'إجمالي المشاهدات', value: totalViewsFromAnalytics || statistics.totalViews, icon: Eye, color: 'text-amber-500', bg: 'bg-amber-50' },

            { label: 'إجمالي الكورسات', value: statistics.totalCourses, icon: Activity, color: 'text-purple-600', bg: 'bg-purple-50' }

          ].map((stat, i) => (

            <div key={i} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-6 lg:p-10 flex items-center gap-5 group hover:shadow-md transition-all">

                <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>

                    <stat.icon size={24} />

                </div>

                <div>

                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{stat.label}</p>

                    <p className="text-2xl font-black text-gray-900 mt-1">{stat.value || 0}</p>

                </div>

            </div>

          ))}

        </div>

      )}



      {/* Search & Global Controls */}

      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">

        <div className="flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1 relative group w-full">

            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />

            <input
              type="text"
              placeholder="ابحث عن درس محدد بالاسم..."
              value={hookSearchTerm}
              onChange={handleSearch}
              className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />

          </div>

          

          <div className="flex items-center gap-3 w-full lg:w-auto">

            <button

              onClick={() => setShowFilters(!showFilters)}

              className={`flex items-center justify-center gap-2 px-6 py-3.5 border rounded-2xl font-bold transition-all ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-600' : 'bg-white border-gray-100 text-gray-500 hover:bg-gray-50'}`}

            >

              <Filter size={18} /> تصفية

            </button>

            

            <div className="flex-1 lg:flex-none flex items-center bg-gray-50 rounded-2xl p-1 border border-gray-100">

                <select

                    value={sortBy}

                    onChange={(e) => setSortBy(e.target.value)}

                    className="bg-transparent px-4 py-2 font-bold text-sm text-gray-700 focus:outline-none appearance-none cursor-pointer"

                >

                    <option value="date">التاريخ</option>

                    <option value="views">المشاهدات</option>

                    <option value="title">العنوان</option>

                </select>

                <button

                    onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}

                    className="p-2.5 bg-white border border-gray-100 rounded-xl shadow-sm text-primary-600 hover:bg-primary-50 transition-all"

                    title={sortOrder === 'desc' ? 'الأقدم أولاً' : 'الأحدث أولاً'}

                >

                    <ArrowUpDown size={16} />

                </button>

            </div>

          </div>

        </div>

        

        {/* Advanced Filter Expansion */}

        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-50 animate-in fade-in slide-in-from-top duration-300">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* المرحلة الدراسية */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">المرحلة الدراسية</label>
                <select
                  value={selectedStage}
                  onChange={(e) => {
                    setSelectedStage(e.target.value);
                    setSelectedGrade(''); // Reset grade when stage changes
                  }}
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700"
                >
                  <option value="">جميع المراحل</option>
                  {filters?.stages?.map((stage) => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </div>

              {/* الصف الدراسي */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">الصف الدراسي</label>
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700"
                >
                  <option value="">جميع الصفوف</option>
                  {allGrades?.map((grade) => (
                    <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
                </select>
              </div>

              {/* المادة العلمية */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">المادة العلمية</label>
                <select
                  value={selectedSubject}
                  onChange={(e) => {
                    setSelectedSubject(e.target.value);
                    setSelectedCourse(''); // Reset course when subject changes
                  }}
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700"
                >
                  <option value="">جميع المواد</option>
                  {filteredSubjects?.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name} - {subject.grade_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* الكورس */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">الكورس</label>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700"
                >
                  <option value="">جميع الكورسات</option>
                  {allCourses?.map((course) => (
                    <option key={course.id} value={course.id}>{course.title}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

      </div>



      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        

        {/* Lessons Main Grid (2/3 Width) */}

        <div className="lg:col-span-2 space-y-6">

            <div className="flex items-center justify-between px-2">

                <h2 className="text-2xl font-black text-gray-900">جميع الدروس المرفوعة</h2>

                <span className="bg-white border border-gray-100 px-4 py-1.5 rounded-full text-xs font-bold text-gray-400 uppercase">

                    الإجمالي: {totalCount || 0}

                </span>

            </div>



            {displayLessons?.length === 0 ? (

                <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200 shadow-sm">

                    <Video size={64} className="text-gray-200 mx-auto mb-6" />

                    <h3 className="text-2xl font-black text-gray-900 mb-2">{hookSearchTerm ? 'لا توجد نتائج لبحثك' : 'لا توجد دروس حالياً'}</h3>

                    <p className="text-gray-500 max-w-sm mx-auto mb-8">
                      {hookSearchTerm ? 'جرب البحث بكلمات أخرى أو قم بإعادة ضبط الفلاتر.' : 'ابدأ الآن ببناء منهجك التعليمي ومشاركة فيديوهاتك مع الطلاب المشتركين.'}
                    </p>

                    <button onClick={() => navigate('/teacher/create-lesson')} className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 flex items-center gap-2 mx-auto">

                        <Plus size={20} /> إنشاء أول درس لك

                    </button>

                </div>

            ) : (

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                {paginatedDisplayLessons?.map((lesson) => {
                  const { duration, views, contentType, badgeColor } = getLessonData(lesson);
                  return (
                    <TeacherLessonCard
                      key={lesson.id}
                      lesson={lesson}
                      duration={duration}
                      views={views}
                      contentType={contentType}
                      badgeColor={badgeColor}
                      onPlay={handlePlayVideo}
                      onDelete={handleDeleteLesson}
                      lessonAnalytics={lessonAnalytics}
                    />
                  );
                })}

                {searchedTotalPages > 1 && (

                  <div className="flex items-center justify-center gap-4 mt-8">

                    <button

                      onClick={() => handlePageChange(currentPage - 1)}

                      disabled={currentPage === 1}

                      className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"

                    >

                      السابق

                    </button>

                    

                    <div className="flex items-center gap-2">

                      {Array.from({ length: searchedTotalPages }, (_, index) => (

                        <button

                          key={index + 1}

                          onClick={() => handlePageChange(index + 1)}

                          className={`w-10 h-10 rounded-xl font-medium transition-all ${

                            currentPage === index + 1

                              ? 'bg-primary-600 text-white'

                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'

                          }`}

                        >

                          {index + 1}

                        </button>

                      ))}

                    </div>

                    

                    <button

                      onClick={() => handlePageChange(currentPage + 1)}

                      disabled={currentPage === searchedTotalPages}

                      className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"

                    >

                      التالي

                    </button>

                  </div>

                )}

                </div>

            )}

        </div>



        {/* Sidebar Analytics (1/3 Width) */}

        <div className="space-y-8">

          {statistics && statistics.topLessons && statistics.topLessons.length > 0 && (

            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 overflow-hidden relative group">
              <div className="absolute top-0 right-0 w-2 h-full bg-primary-600/20 group-hover:bg-primary-600 transition-colors duration-500"></div>
              <div className="flex items-center gap-3 mb-8">
                <div className="bg-amber-50 p-2.5 rounded-xl text-amber-500 shadow-inner">

                  <TrendingUp size={20} />

                </div>
                <h2 className="text-xl font-black text-gray-900">الأكثر مشاهدة</h2>
              </div>

              <div className="space-y-4">

                {statistics.topLessons && statistics.topLessons.length > 0 ? (
                  statistics.topLessons

                    .sort((a, b) => {

                      // Primary sort: by view count
                      const aViews = parseInt(a.view_count) || 0;
                      const bViews = parseInt(b.view_count) || 0;
                      if (bViews !== aViews) return bViews - aViews;
                    
                      // Secondary sort: by completion count

                      const aCompletions = parseInt(a.completion_count) || 0;
                      const bCompletions = parseInt(b.completion_count) || 0;
                      if (bCompletions !== aCompletions) return bCompletions - aCompletions;

                      

                      // Tertiary sort: by title alphabetically

                      return a.title.localeCompare(b.title);

                    })

                    .map((lesson, index) => {

                      const views = getLessonViews(lesson);

                      const viewCount = parseInt(lesson.view_count) || 0;

                      const completionCount = parseInt(lesson.completion_count) || 0;

                      

                      return (

                        <div key={lesson.id} className={`flex items-center justify-between p-4 rounded-3xl border transition-all group/item shadow-sm ${

                          index === 0 ? 'bg-amber-50 border-amber-200 hover:border-amber-300 hover:bg-amber-100' : 

                          'bg-gray-50/50 border-transparent hover:border-amber-100 hover:bg-white'

                        }`}>

                          <div className="flex items-center gap-4">
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-xs shadow-sm ${
                              index === 0 ? 'bg-amber-500 text-white' : 
                              viewCount > 1 ? 'bg-primary-100 text-primary-600' : 'bg-white text-gray-400'

                            }`}>

                              {index + 1}

                            </div>

                            <div className="min-w-0">
                              <p className={`font-bold line-clamp-1 group-hover/item:text-primary-600 transition-colors ${
                                index === 0 ? 'text-gray-900' : 'text-gray-800'

                              }`}>{lesson.title}</p>

                              <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">{lesson.course_title}</p>

                            </div>

                          </div>

                          <div className="flex flex-col items-end">

                            <div className="flex items-center gap-2">

                              <span className={`text-sm font-black ${

                                viewCount > 1 ? 'text-primary-600' : 'text-gray-800'

                              }`}>{views}</span>

                              {viewCount > 1 && (

                                <span className="bg-amber-100 text-amber-600 text-[8px] px-1.5 py-0.5 rounded-full font-black">الأعلى</span>

                              )}

                            </div>

                            <span className="text-[8px] font-black text-gray-400 uppercase">مشاهدة</span>

                            {completionCount > 0 && (

                              <span className="text-[8px] font-black text-green-600 uppercase">{completionCount} إكمال</span>

                            )}

                          </div>

                        </div>

                      );

                    })

                ) : (

                  <div className="text-center py-8 text-gray-400">

                    <TrendingUp size={32} className="mx-auto mb-2 opacity-50" />

                    <p className="text-sm font-medium">لا توجد بيانات مشاهدات كافية</p>

                    <p className="text-xs mt-1">سيظهر هنا الدروس الأكثر مشاهدة عندما تبدأ الطلاب في المشاهدة</p>

                  </div>

                )}

              </div>

            </div>

          )}



          {/* Motivational Support Card */}

          <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">

            <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>

            <div className="relative z-10 space-y-6">

              <div className="bg-primary-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-xl">

                <BarChart3 size={24} />

              </div>

              <h3 className="text-2xl font-black leading-tight">تتبع أداء محتواك التعليمي بدقة</h3>

              <p className="text-gray-400 text-sm font-medium leading-relaxed">

                قم بمراجعة إحصائيات كل درس بشكل منفصل لتحسين جودة الشرح وزيادة تفاعل الطلاب مع الكورسات الخاصة بك.

              </p>

              <button className="bg-white text-gray-900 px-6 py-3 rounded-2xl font-black text-sm hover:bg-primary-50 transition-all flex items-center gap-2 shadow-xl">

                التقارير التفصيلية <ChevronLeft size={18} />

              </button>

            </div>

          </div>

        </div>

      </div>



      {/* Enhanced Video Playback Modal */}

      {showVideoModal && selectedVideo && (

        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-500">

          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-5xl w-full relative overflow-hidden">

            <div className="flex items-center justify-between p-6 border-b border-gray-100">

              <div className="flex items-center gap-3">

                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg ${
                  selectedVideo?.content_type === 'pdf' ? 'bg-red-600' :
                  selectedVideo?.content_type === 'file' ? 'bg-blue-600' :
                  selectedVideo?.content_type === 'qa' || selectedVideo?.content_type === 'quiz' ? 'bg-purple-600' :
                  'bg-primary-600'
                }`}>

                  {selectedVideo?.content_type === 'pdf' ? (
                    <FileText size={20} />
                  ) : selectedVideo?.content_type === 'file' ? (
                    <Download size={20} />
                  ) : selectedVideo?.content_type === 'qa' || selectedVideo?.content_type === 'quiz' ? (
                    <Activity size={20} />
                  ) : (
                    <Play size={20} fill="currentColor" />
                  )}

                </div>

                <h3 className="text-xl font-black text-gray-900">{selectedVideo.title}</h3>

              </div>

              <button onClick={handleCloseVideoModal} className="w-10 h-10 flex items-center justify-center bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all">

                <X size={20} />

              </button>

            </div>

            

            <div className="p-8">

              {/* Determine content type and display accordingly */}

              {selectedVideo?.content_type === 'pdf' ? (
                // PDF Display - using signed Bunny URL via Google Viewer
                <PdfPreviewInModal lessonId={selectedVideo.id} />

              ) : selectedVideo?.content_type === 'file' ? (

                // File Download Display

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-12 flex flex-col items-center justify-center gap-6">

                  <div className="bg-blue-600 w-20 h-20 rounded-full flex items-center justify-center shadow-lg">

                    <FileVideo size={40} className="text-white" />

                  </div>

                  <h4 className="text-xl font-black text-gray-900">ملف تحميل</h4>

                  <p className="text-gray-600 text-center max-w-md">

                    يمكنك تحميل الملف المرفق لهذا الدرس والعمل عليه محلياً

                  </p>

                  <a

                    href={`${getServerUrl()}${selectedVideo.video_url}`}

                    download

                    className="bg-blue-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-blue-700 transition-all flex items-center gap-2"

                  >

                    <Download size={20} />

                    تحميل الملف

                  </a>

                </div>

              ) : selectedVideo?.content_type === 'qa' || selectedVideo?.content_type === 'quiz' ? (

                // Q&A / Quiz Display

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-12 flex flex-col items-center justify-center gap-6">

                  <div className="bg-purple-600 w-20 h-20 rounded-full flex items-center justify-center shadow-lg">

                    <FileVideo size={40} className="text-white" />

                  </div>

                  <h4 className="text-xl font-black text-gray-900">أسئلة واختبارات</h4>

                  <p className="text-gray-600 text-center max-w-md">

                    يتم فتح الأسئلة والاختبارات في صفحة منفصلة لضمان أفضل تجربة

                  </p>

                  <button

                    onClick={() => {

                      navigate(`/lessons/${selectedVideo.id}`);

                      handleCloseVideoModal();

                    }}

                    className="bg-purple-600 text-white px-8 py-3 rounded-2xl font-bold hover:bg-purple-700 transition-all flex items-center gap-2"

                  >

                    افتح الاختبار الآن →

                  </button>

                </div>

              ) : (

                // Default: Video Display

                <div className="aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl ring-8 ring-gray-50">

                  {(() => {

                    const videoUrl = selectedVideo.video_url || selectedVideo.url;

                    

                    if (!videoUrl) {

                      return (

                        <div className="w-full h-full flex flex-col items-center justify-center text-white gap-4">

                          <FileVideo size={80} className="opacity-20" />

                          <p className="font-bold opacity-80 uppercase tracking-widest">عذراً، محتوى الفيديو غير متوفر</p>

                          <p className="text-sm opacity-60">لا يوجد رابط فيديو لهذا الدرس</p>

                        </div>

                      );

                    }

                    

                    // Bunny Stream iframe URLs

                    if (videoUrl.includes('iframe.mediadelivery.net')) {

                      return (

                        <iframe

                          title="Bunny Stream Video"

                          src={videoUrl}

                          className="w-full h-full"

                          frameBorder="0"

                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"

                          allowFullScreen

                        />

                      );

                    }

                    

                    // Check for external videos (YouTube, Vimeo, etc.)

                    if (videoUrl.includes('http') && !videoUrl.includes('iframe.mediadelivery.net')) {

                      return (

                        <iframe

                          title="External Video Player"

                          src={getVideoEmbedUrl(videoUrl)}

                          className="w-full h-full"

                          frameBorder="0"

                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"

                          allowFullScreen

                        />

                      );

                    }

                    

                    // Default to video element for uploaded videos

                    return (

                      <video 

                        src={`${getServerUrl()}${videoUrl}`} 

                        className="w-full h-full" 

                        controls 

                        autoPlay 

                      />

                    );

                  })()}

                </div>
              )}

              

              {/* Display stats in modal */}

              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">

  {/* المشاهدات — فقط لدروس Bunny */}
  {selectedVideo?.bunny_video_id && (
    <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
      <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">المشاهدات</p>
      <p className="text-xl font-black text-gray-900">
        {lessonAnalytics[selectedVideo.id] ?? getLessonViews(selectedVideo) ?? 0}
      </p>
    </div>
  )}

  {/* المدة — فقط للدروس اللي عندها وقت */}
  {getLessonDuration(selectedVideo) > 0 && (
    <div className="bg-emerald-50 p-4 rounded-2xl border border-emerald-100">
      <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">المدة</p>
      <p className="text-xl font-black text-gray-900">{formatDuration(getLessonDuration(selectedVideo))}</p>
    </div>
  )}

  {/* التاريخ — دايماً */}
  <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100">
    <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">التاريخ</p>
    <p className="text-sm font-bold text-gray-900">
      {selectedVideo?.created_at ? new Date(selectedVideo.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
    </p>
  </div>

  {/* النوع — دايماً */}
  <div className="bg-purple-50 p-4 rounded-2xl border border-purple-100">
    <p className="text-[10px] font-black text-purple-600 uppercase tracking-widest mb-1">النوع</p>
    <p className="text-sm font-bold text-gray-900">
      {selectedVideo?.bunny_video_id ? 'Video' :
       selectedVideo?.video_url?.includes('youtube') ? 'YouTube' :
       selectedVideo?.video_url?.includes('vimeo') ? 'Vimeo' :
       selectedVideo?.content_type === 'pdf' ? 'PDF' :
       selectedVideo?.content_type === 'file' ? 'ملف' : 'خارجي'}
    </p>
             </div>
            </div>
            </div>
          </div>
       </div>
      )}

      {/* Confirmation Components Integration */}
      <ConfirmDialogComponent />
    </div>
  );
};

export default Lessons;